import { lazy, Suspense, useState, useEffect, useLayoutEffect } from "react";
import { Switch, Route, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";

const GalleryPage = lazy(() => import("@/pages/gallery"));
const ContactPage = lazy(() => import("@/pages/contact"));
const BlogPage = lazy(() => import("@/pages/blog"));
const BlogPostPage = lazy(() => import("@/pages/blog-post"));
const ResourcesPage = lazy(() => import("@/pages/resources"));
const ResourcesPostPage = lazy(() => import("@/pages/resources-post"));
const HostHQPage = lazy(() => import("@/pages/hosthq"));
const AdminLogin = lazy(() => import("@/pages/admin/login"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminCalendar = lazy(() => import("@/pages/admin/calendar"));
const AdminInbox = lazy(() => import("@/pages/admin/inbox"));
const AdminSettings = lazy(() => import("@/pages/admin/settings"));
const AdminEarnings = lazy(() => import("@/pages/admin/earnings"));
const AdminListing = lazy(() => import("@/pages/admin/listing"));
const AdminPreferences = lazy(() => import("@/pages/admin/preferences"));
const AdminChannels = lazy(() => import("@/pages/admin/channels"));
const AdminPricing = lazy(() => import("@/pages/admin/pricing"));
const AdminIntegrations = lazy(() => import("@/pages/admin/integrations"));
const AdminSmartLocks = lazy(() => import("@/pages/admin/smart-locks"));
const AdminPromptBuilder = lazy(() => import("@/pages/admin/prompt-builder"));
const AdminMenu = lazy(() => import("@/pages/admin/menu"));
const AdminLiveChat = lazy(() => import("@/pages/admin/live-chat"));
const AdminAiKnowledge = lazy(() => import("@/pages/admin/ai-knowledge"));
const AdminConversations = lazy(() => import("@/pages/admin/conversations"));
const AdminGuests = lazy(() => import("@/pages/admin/guests"));
const BookingLookup = lazy(() => import("@/pages/booking-lookup"));
const BookingInvite = lazy(() => import("@/pages/booking-invite"));
const CabinRentalAgreementPage = lazy(() => import("@/pages/cabin-rental-agreement"));
const CabinPaymentPage = lazy(() => import("@/pages/cabin-payment"));
const AdminBlog = lazy(() => import("@/pages/admin/blog"));
const AdminHostHQBlog = lazy(() => import("@/pages/admin/hosthq-blog"));
const AdminDiscountCodes = lazy(() => import("@/pages/admin/discount-codes"));
const AdminPromote = lazy(() => import("@/pages/admin/promote"));
const AdminProperties = lazy(() => import("@/pages/admin/properties"));
const AdminAnalytics = lazy(() => import("@/pages/admin/analytics"));
const AdminGrowth = lazy(() => import("@/pages/admin/growth"));
const AdminExperiments = lazy(() => import("@/pages/admin/experiments"));
const AdminReplay = lazy(() => import("@/pages/admin/replay"));
const AdminLeads = lazy(() => import("@/pages/admin/leads"));
const AdminHeatmaps = lazy(() => import("@/pages/admin/heatmaps"));
const AdminMarketAnalysis = lazy(() => import("@/pages/admin/market-analysis"));
const AdminExecutiveBriefing = lazy(() => import("@/pages/admin/executive-briefing"));
const AdminCustomPaymentLinks = lazy(() => import("@/pages/admin/custom-payment-links"));
const AdminCampaigns = lazy(() => import("@/pages/admin/campaigns"));
const AdminOutreach = lazy(() => import("@/pages/admin/outreach"));
const LakeMiltonaPage = lazy(() => import("@/pages/lake-miltona"));
const AlexandriaMnPage = lazy(() => import("@/pages/alexandria-mn"));
const MinnesotaCabinPage = lazy(() => import("@/pages/minnesota-cabin"));
const ListingPage = lazy(() => import("@/pages/listing"));
const PontoonPage = lazy(() => import("@/pages/pontoon"));
const PontoonCheckinPage = lazy(() => import("@/pages/pontoon-checkin"));
const PontoonSafetyPage = lazy(() => import("@/pages/PontoonSafetyPage"));
const BookDirectPage = lazy(() => import("@/pages/book-direct"));
const BookPage = lazy(() => import("@/pages/book"));
const ExplorePage = lazy(() => import("@/pages/explore"));
const MediaKitPage = lazy(() => import("@/pages/media-kit"));
const GoRedirect = lazy(() => import("@/pages/go"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const LaunchPromoBanner = lazy(() => import("@/components/LaunchPromoBanner"));
const Experimentation = lazy(() => import("@/components/Experimentation"));
import { usePageTracking, useFrustrationTracking, usePresenceHeartbeat } from "@/hooks/use-page-tracking";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/gallery" component={GalleryPage} />
      <Route path="/availability">{() => <Redirect to="/#booking" />}</Route>
      <Route path="/rates">{() => <Redirect to="/#booking" />}</Route>
      <Route path="/amenities">{() => <Redirect to="/#amenities" />}</Route>
      <Route path="/reviews">{() => <Redirect to="/#reviews" />}</Route>
      <Route path="/location">{() => <Redirect to="/#location" />}</Route>
      <Route path="/contact" component={ContactPage} />
      <Route path="/my-booking" component={BookingLookup} />
      <Route path="/my-booking/invite" component={BookingInvite} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route path="/hosthq" component={HostHQPage} />
      <Route path="/hosthq/blog" component={ResourcesPage} />
      <Route path="/hosthq/blog/:slug" component={ResourcesPostPage} />
      <Route path="/resources" component={ResourcesPage} />
      <Route path="/resources/:slug" component={ResourcesPostPage} />
      <Route path="/admin" component={AdminLogin} />
      <Route path="/admin/dashboard" component={AdminDashboard} />
      <Route path="/admin/calendar" component={AdminCalendar} />
      <Route path="/admin/inbox" component={AdminInbox} />
      <Route path="/admin/settings" component={AdminSettings} />
      <Route path="/admin/earnings" component={AdminEarnings} />
      <Route path="/admin/listing" component={AdminListing} />
      <Route path="/admin/preferences" component={AdminPreferences} />
      <Route path="/admin/channels" component={AdminChannels} />
      <Route path="/admin/pricing" component={AdminPricing} />
      <Route path="/admin/integrations" component={AdminIntegrations} />
      <Route path="/admin/smart-locks" component={AdminSmartLocks} />
      <Route path="/admin/prompt-builder" component={AdminPromptBuilder} />
      <Route path="/admin/menu" component={AdminMenu} />
      <Route path="/admin/live-chat" component={AdminLiveChat} />
      <Route path="/admin/ai-knowledge" component={AdminAiKnowledge} />
      <Route path="/admin/conversations" component={AdminConversations} />
      <Route path="/admin/guests" component={AdminGuests} />
      <Route path="/admin/blog" component={AdminBlog} />
      <Route path="/admin/hosthq-blog" component={AdminHostHQBlog} />
      <Route path="/admin/discount-codes" component={AdminDiscountCodes} />
      <Route path="/admin/promote" component={AdminPromote} />
      <Route path="/admin/properties" component={AdminProperties} />
      <Route path="/admin/analytics" component={AdminAnalytics} />
      <Route path="/admin/growth" component={AdminGrowth} />
      <Route path="/admin/experiments" component={AdminExperiments} />
      <Route path="/admin/replay" component={AdminReplay} />
      <Route path="/admin/leads" component={AdminLeads} />
      <Route path="/admin/heatmaps" component={AdminHeatmaps} />
      <Route path="/admin/market-analysis" component={AdminMarketAnalysis} />
      <Route path="/admin/executive-briefing" component={AdminExecutiveBriefing} />
      <Route path="/admin/payment-links" component={AdminCustomPaymentLinks} />
      <Route path="/admin/campaigns" component={AdminCampaigns} />
      <Route path="/admin/outreach" component={AdminOutreach} />
      <Route path="/listings/:slug" component={ListingPage} />
      <Route path="/pontoon" component={PontoonPage} />
      <Route path="/pontoon-checkin" component={PontoonCheckinPage} />
      <Route path="/pontoon-safety" component={PontoonSafetyPage} />
      <Route path="/lake-miltona" component={LakeMiltonaPage} />
      <Route path="/alexandria-mn" component={AlexandriaMnPage} />
      <Route path="/minnesota-cabin" component={MinnesotaCabinPage} />
      <Route path="/book" component={BookPage} />
      <Route path="/book-direct" component={BookDirectPage} />
      <Route path="/rental-agreement" component={CabinRentalAgreementPage} />
      <Route path="/cabin-payment/:id" component={CabinPaymentPage} />
      <Route path="/explore" component={ExplorePage} />
      <Route path="/media-kit" component={MediaKitPage} />
      <Route path="/press">{() => <Redirect to="/media-kit" />}</Route>
      <Route path="/go/:slug" component={GoRedirect} />
      <Route component={NotFound} />
    </Switch>
  );
}

function DeferredChatWidget() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setShow(true));
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
}

function TrackedApp() {
  usePageTracking();
  useFrustrationTracking();
  usePresenceHeartbeat();
  const [location] = useLocation();
  const isAdmin = location.startsWith("/admin") || location.startsWith("/hosthq");
  return (
    <>
      <Toaster />
      {!isAdmin && (
        <Suspense fallback={null}>
          <LaunchPromoBanner />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <Router />
      </Suspense>
      <DeferredChatWidget />
      {!isAdmin && (
        <Suspense fallback={null}>
          <Experimentation />
        </Suspense>
      )}
    </>
  );
}

// SSR routes (blog posts, gallery, rates, etc.) inject a visually-hidden,
// crawler-facing copy of the page content as a sibling of #root
// (#ssr-route-prerender / #ssr-home-prerender) so non-JS crawlers get real
// HTML in the initial response. React's createRoot() only ever manages
// #root, so that sibling would otherwise stick around forever once the
// client app hydrates, doubling the live DOM (two <article>s, two <h1>s,
// etc). Remove it after mounting, except blog pages which wait for content.
// This runs once per full page load, generically for any route using this
// pattern, and never touches the pre-rendered HTML a non-JS crawler sees in
// the raw initial response (that HTML is already served before this JS runs).
function useRemoveSsrPrerender() {
  // useLayoutEffect (not useEffect) so the visible mobile static hero is removed
  // in the same commit React mounts its tree — before the browser paints — so the
  // static splash and the live app are never both painted (no duplicate flash).
  useLayoutEffect(() => {
    const isBlogRoute = /^\/blog(?:\/|$)/.test(window.location.pathname);
    ["static-hero", "ssr-route-prerender", "ssr-home-prerender"].forEach((id) => {
      // Blog pages remove their fallback only after their real content mounts.
      if (id === "ssr-route-prerender" && isBlogRoute) return;
      document.getElementById(id)?.remove();
    });
  }, []);
}

function App() {
  useRemoveSsrPrerender();
  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <TrackedApp />
          </WouterRouter>
        </TooltipProvider>
      </AdminAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
